/**
 * Computes a robust, ideally document-unique CSS selector for an element.
 * Class names are deliberately avoided (Tailwind/hashed classes churn);
 * stable hooks (id, data-testid, data-id, name) are preferred.
 */

const AUTO_GENERATED_ID = /[0-9a-f]{8,}|^radix-|^:r/i;
const STABLE_ATTRIBUTES = ['data-testid', 'data-id', 'name'] as const;
const MAX_DEPTH = 12;

function isUnique(selector: string): boolean {
  try {
    return document.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

function isUsableId(id: string): boolean {
  return id.length > 0 && !AUTO_GENERATED_ID.test(id) && isUnique(`#${CSS.escape(id)}`);
}

function segmentFor(node: Element): string {
  const tag = node.tagName.toLowerCase();

  for (const attr of STABLE_ATTRIBUTES) {
    const value = node.getAttribute(attr);
    if (value) {
      return `${tag}[${attr}="${CSS.escape(value)}"]`;
    }
  }

  const parent = node.parentElement;
  if (!parent) return tag;
  const sameTagSiblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
  if (sameTagSiblings.length === 1) return tag;
  return `${tag}:nth-of-type(${sameTagSiblings.indexOf(node) + 1})`;
}

export function computeSelector(el: Element): string {
  if (el.id && isUsableId(el.id)) {
    return `#${CSS.escape(el.id)}`;
  }

  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;

  while (node && node !== document.body && node !== document.documentElement) {
    if (node.id && isUsableId(node.id)) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }
    parts.unshift(segmentFor(node));
    const candidate = parts.join(' > ');
    if (isUnique(candidate)) return candidate;
    depth += 1;
    if (depth >= MAX_DEPTH) break;
    node = node.parentElement;
  }

  // Best effort: selector is diagnostic metadata, not a correctness contract.
  return parts.join(' > ') || el.tagName.toLowerCase();
}
